import React, { useEffect, useState, useRef } from "react";
import Select from "react-select";
import { debounce } from "lodash";
import Header from "../../components/Header";
import {
  Exercise,
  searchExercisesId,
  getExerciseSwaps,
  addExerciseSwap,
  removeExerciseSwap,
} from "../../services/exerciseService";

interface ExerciseOption {
  value: number;
  label: string;
}

const ExerciseSwapsPage: React.FC = () => {
  const [selectedExercise, setSelectedExercise] = useState<ExerciseOption | null>(null);
  const [exerciseOptions, setExerciseOptions] = useState<ExerciseOption[]>([]);
  const [swaps, setSwaps] = useState<Exercise[]>([]);
  const [swapOptions, setSwapOptions] = useState<ExerciseOption[]>([]);
  const [selectedSwap, setSelectedSwap] = useState<ExerciseOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchMain = useRef(
    debounce(async (query: string) => {
      if (!query.trim()) return;
      const results = await searchExercisesId("name", query);
      setExerciseOptions(results.map((e) => ({ value: e.id, label: e.name })));
    }, 400)
  ).current;

  const searchSwap = useRef(
    debounce(async (query: string) => {
      if (!query.trim()) return;
      const results = await searchExercisesId("name", query);
      setSwapOptions(results.map((e) => ({ value: e.id, label: e.name })));
    }, 400)
  ).current;

  useEffect(() => {
    if (!selectedExercise) {
      setSwaps([]);
      return;
    }
    setLoading(true);
    setError(null);
    getExerciseSwaps(selectedExercise.value)
      .then(setSwaps)
      .catch(() => setError("Failed to load swaps"))
      .finally(() => setLoading(false));
  }, [selectedExercise]);

  const handleAddSwap = async () => {
    if (!selectedExercise || !selectedSwap) return;
    if (selectedSwap.value === selectedExercise.value) {
      setError("Cannot swap an exercise with itself");
      return;
    }
    try {
      const updated = await addExerciseSwap(selectedExercise.value, selectedSwap.value);
      setSwaps(updated);
      setSelectedSwap(null);
      setSwapOptions([]);
      setError(null);
    } catch {
      setError("Failed to add swap");
    }
  };

  const handleRemoveSwap = async (swapId: number) => {
    if (!selectedExercise) return;
    try {
      const updated = await removeExerciseSwap(selectedExercise.value, swapId);
      setSwaps(updated);
    } catch {
      setError("Failed to remove swap");
    }
  };

  return (
    <div className="flex flex-col h-screen">
      <Header title="Exercise Swaps" />
      <div className="flex-1 p-6 overflow-auto">
        <div className="max-w-2xl mx-auto space-y-6">

          {/* Select main exercise */}
          <div className="bg-white rounded-xl shadow-sm p-5">
            <h2 className="text-sm font-semibold text-gray-700 mb-3">
              Select Exercise
            </h2>
            <Select<ExerciseOption>
              options={exerciseOptions}
              value={selectedExercise}
              onChange={(opt) => {
                setSelectedExercise(opt);
                setSelectedSwap(null);
              }}
              onInputChange={(val) => { searchMain(val); }}
              placeholder="Search exercise..."
              isClearable
            />
          </div>

          {/* Swaps list */}
          {selectedExercise && (
            <div className="bg-white rounded-xl shadow-sm p-5">
              <h2 className="text-sm font-semibold text-gray-700 mb-3">
                Swap Options for{" "}
                <span className="text-yellow-600">{selectedExercise.label}</span>
              </h2>

              {error && (
                <p className="text-red-500 text-sm mb-3">{error}</p>
              )}

              {loading ? (
                <p className="text-sm text-gray-400">Loading...</p>
              ) : swaps.length === 0 ? (
                <p className="text-sm text-gray-400 italic">No swap options yet</p>
              ) : (
                <ul className="divide-y divide-gray-100 mb-4">
                  {swaps.map((swap) => (
                    <li
                      key={swap.id}
                      className="flex items-center justify-between py-2"
                    >
                      <span className="text-sm text-gray-800">{swap.name}</span>
                      <button
                        onClick={() => handleRemoveSwap(swap.id)}
                        className="text-xs text-red-400 hover:text-red-600 transition-colors"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Add swap */}
              <div className="flex gap-2 items-center mt-3 pt-3 border-t border-gray-100">
                <div className="flex-1">
                  <Select<ExerciseOption>
                    options={swapOptions.filter(
                      (o) =>
                        o.value !== selectedExercise.value &&
                        !swaps.find((s) => s.id === o.value)
                    )}
                    value={selectedSwap}
                    onChange={(opt) => setSelectedSwap(opt)}
                    onInputChange={(val) => { searchSwap(val); }}
                    placeholder="Search swap exercise..."
                    isClearable
                  />
                </div>
                <button
                  onClick={handleAddSwap}
                  disabled={!selectedSwap}
                  className="px-4 py-2 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  Add
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExerciseSwapsPage;
